import { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';

const EXAMPLES = [
  {
    before: 'Clean the bathroom',
    after: ['Gather supplies from closet', 'Wipe mirror & countertop', 'Scrub toilet inside & out', 'Clean the bathtub/shower', 'Mop the floor & take out trash'],
  },
  {
    before: 'Move to a new apartment',
    after: ['Sort items: keep / donate / toss', 'Pack one room at a time', 'Book a moving van or helpers', 'Set up utilities at new place', 'Unpack essentials first night'],
  },
  {
    before: 'Prepare for a job interview',
    after: ['Research the company & role', 'Practice 5 common questions aloud', 'Pick and iron your outfit', 'Print resume copies + directions', 'Set 2 alarms for the morning'],
  },
  {
    before: 'Plan a birthday party',
    after: ['Pick a date & send invites', 'Choose a theme or color scheme', 'Order cake & snacks', 'Set up decorations & playlist', 'Prep party favors / thank-yous'],
  },
];

const ENCOURAGEMENTS = [
  "Your brain works differently. That's a feature, not a bug.",
  "Breaking it down so you don't have to.",
  "Small steps still move you forward.",
  "You don't need motivation. You need a first step.",
  "One micro-task at a time. You've got this.",
];

function AILoadingOverlay({ theme }) {
  const [exampleIdx, setExampleIdx] = useState(0);
  const [encourageIdx, setEncourageIdx] = useState(0);
  const [fade, setFade] = useState(true);
  const [revealCount, setRevealCount] = useState(0);

  // Cycle examples every 5s
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setExampleIdx((prev) => (prev + 1) % EXAMPLES.length);
        setEncourageIdx((prev) => (prev + 1) % ENCOURAGEMENTS.length);
        setRevealCount(0);
        setFade(true);
      }, 400);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Stagger reveal subtasks
  useEffect(() => {
    const ex = EXAMPLES[exampleIdx];
    if (revealCount < ex.after.length) {
      const timer = setTimeout(() => setRevealCount((c) => c + 1), 350);
      return () => clearTimeout(timer);
    }
  }, [revealCount, exampleIdx]);

  const example = EXAMPLES[exampleIdx];

  return (
    <div
      className={`rounded-xl border ${theme?.colors.border || 'border-stone-200'} ${theme?.colors.surface || 'bg-white'} p-6 sm:p-8 overflow-hidden`}
      data-testid="ai-loading-overlay"
    >
      {/* Pulsing header */}
      <div className="flex items-center justify-center gap-3 mb-6">
        <div className="relative">
          <Sparkles className="w-6 h-6 text-[#E07A5F] animate-pulse" />
          <div className="absolute inset-0 w-6 h-6 bg-[#E07A5F]/20 rounded-full animate-ping" />
        </div>
        <p
          className={`text-lg sm:text-xl font-medium ${theme?.colors.textPrimary || 'text-stone-900'}`}
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          Breaking it down for you...
        </p>
      </div>

      {/* Progress dots animation */}
      <div className="flex justify-center gap-2 mb-8">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="w-2 h-2 rounded-full bg-[#E07A5F]"
            style={{
              animation: 'bounce 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
      </div>

      {/* Before / After example slideshow */}
      <div
        className="transition-opacity duration-400"
        style={{ opacity: fade ? 1 : 0 }}
      >
        {/* Before */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`text-xs uppercase tracking-widest font-semibold ${theme?.colors.textSecondary || 'text-stone-400'}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Before
          </div>
          <div className={`flex-1 h-px ${theme?.colors.border || 'bg-stone-200'}`} style={{ backgroundColor: 'currentColor', opacity: 0.2 }} />
        </div>
        <div className={`${theme?.colors.bg || 'bg-stone-100'} rounded-lg p-3 sm:p-4 mb-5`}>
          <p
            className={`text-base sm:text-lg font-medium ${theme?.colors.textPrimary || 'text-stone-800'}`}
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {example.before}
          </p>
        </div>

        {/* After */}
        <div className="flex items-center gap-2 mb-4">
          <div className="text-xs uppercase tracking-widest font-semibold text-[#81B29A]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            After
          </div>
          <div className="flex-1 h-px bg-[#81B29A]/20" />
        </div>
        <div className="space-y-2">
          {example.after.map((step, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 ${theme?.colors.bg || 'bg-stone-100'} rounded-lg p-2.5 sm:p-3 transition-all duration-300`}
              style={{
                opacity: i < revealCount ? 1 : 0,
                transform: i < revealCount ? 'translateX(0)' : 'translateX(-12px)',
              }}
            >
              <div className="w-5 h-5 rounded-full border-2 border-[#81B29A]/40 flex-shrink-0" />
              <span
                className={`text-sm sm:text-base ${theme?.colors.textPrimary || 'text-stone-700'}`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {step}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Encouragement */}
      <p
        className={`text-center text-sm mt-6 italic ${theme?.colors.textSecondary || 'text-stone-400'} transition-opacity duration-400`}
        style={{ fontFamily: 'IBM Plex Sans, sans-serif', opacity: fade ? 1 : 0 }}
      >
        {ENCOURAGEMENTS[encourageIdx]}
      </p>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default AILoadingOverlay;
